import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import config from '../../config/config';
import DataTable from 'react-data-table-component';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DashboardLayout from '../shared/layout/DashboardLayout';
import EmailReminderBanner from '../../components/common/EmailReminderBanner';
import { FaEye, FaTrash } from "react-icons/fa";
import { Tooltip } from 'react-tooltip';
import '../../styles/App.css';
import DataChart from '../../components/common/DataChart';
import { getKLMenuItems } from '../../utils/menuItems';

const UserPage = () => {
  const tableRef = useRef(null);
  const [dataList, setDataList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [ticketNoToDelete, setTicketNoToDelete] = useState(null);
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterTitle, setFilterTitle] = useState('Semua Data');
  const [stats, setStats] = useState({
    totalData: 0,
    integratedData: 0,
    nonIntegratedData: 0,
    totalUniqueIGT: 0,
    uniqueIntegratedIGT: 0 // Tambahan state baru untuk IGT terintegrasi unik
  });

  // State untuk informasi IGT
  const [igtData, setIgtData] = useState([]);
  const [expandedIGT, setExpandedIGT] = useState(null);
  const [igtTableData, setIgtTableData] = useState([]);
  const [isGroupView, setIsGroupView] = useState(false);

  // Fetch user profile untuk cek email
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setUserEmail('');
          return;
        }
        
        const response = await axios.get(`${config.API_URL}/profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setUserEmail(response.data.email || '');
      } catch (err) {
        console.error('Gagal mengambil data profil:', err);
        setUserEmail(''); 
      }
    };
    fetchProfile();
  }, []);

  // Fungsi untuk ekstrak kode IGT dari nomor tiket
  const extractIGTCode = (noTiket) => {
    if (!noTiket || typeof noTiket !== 'string') return null;

    // Jika ada tanda "-", ambil bagian sebelum tanda "-"
    if (noTiket.includes('-')) {
      const parts = noTiket.split('-');
      return parts[0].substring(0, 6);
    }

    // Jika tidak ada tanda "-", ambil 6 digit pertama saja
    return noTiket.substring(0, 6);
  };

  // Fungsi untuk menghitung total IGT unik dan IGT terintegrasi unik
  const calculateIGTStats = (data) => {
    const igtCodes = new Set();
    const integratedIGTCodes = new Set();
    const igtMap = new Map();

    data.forEach(item => {
      const igtCode = extractIGTCode(item.no_tiket);
      if (igtCode) {
        igtCodes.add(igtCode);

        // Jika status terintegrasi, tambahkan ke set integratedIGTCodes
        if (item.status === 'Sudah Terintegrasi') {
          integratedIGTCodes.add(igtCode);
        }

        // Tambahkan atau perbarui informasi IGT dalam map
        if (!igtMap.has(igtCode)) {
          igtMap.set(igtCode, {
            code: igtCode,
            count: 1,
            integratedCount: item.status === 'Sudah Terintegrasi' ? 1 : 0,
            nonIntegratedCount: item.status !== 'Sudah Terintegrasi' ? 1 : 0,
            records: [item],
            referenceNamaIgt: item.nama_igt
          });
        } else {
          const igtInfo = igtMap.get(igtCode);
          igtInfo.count += 1;
          if (item.status === 'Sudah Terintegrasi') {
            igtInfo.integratedCount += 1;
          } else {
            igtInfo.nonIntegratedCount += 1;
          }
          igtInfo.records.push(item);
        }
      }
    });

    // Konversi Map menjadi array untuk digunakan di state
    const igtArray = Array.from(igtMap.values()).sort((a, b) => b.count - a.count);
    setIgtData(igtArray);

    return {
      totalUniqueIGT: igtCodes.size,
      uniqueIntegratedIGT: integratedIGTCodes.size
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);
        if (!token || !decoded.twoFactorVerified) {
          navigate('/');
          return;
        }

        setUsername(decoded.username);
        setUserId(decoded.userId);

        let combined = [];
        if (isGroupView) {
          // Hanya Data Saya
          const personalResp = await axios.get(`${config.API_URL}/data?userId=${decoded.userId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          combined = personalResp.data;
        } else {
          // Gabungkan Data KL + Data Saya
          const [groupResult, personalResult] = await Promise.allSettled([
            axios.get(`${config.API_URL}/data/group?userId=${decoded.userId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
            axios.get(`${config.API_URL}/data?userId=${decoded.userId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
          ]);

          const groupData = groupResult.status === 'fulfilled' ? groupResult.value.data : [];
          const personalData = personalResult.status === 'fulfilled' ? personalResult.value.data : [];
          const mapByTicket = new Map();
          [...groupData, ...personalData].forEach(item => {
            mapByTicket.set(item.no_tiket, item);
          });
          combined = Array.from(mapByTicket.values());
        }

        const modifiedData = combined.map(item => {
          if (item.status === 'Draft') {
            return { ...item, status: 'Sedang Verifikasi' };
          }
          return item;
        });

        // Tambahkan informasi kepemilikan dan updater
        const enrichedData = modifiedData.map(item => ({
          ...item,
          userid: item.userid || decoded.userId,
          uploader_username: item.uploader_username || decoded.username,
          last_updater_username: item.last_updater_username || item.uploader_username || decoded.username,
        }));

        setError('');
        setDataList(enrichedData);
        setFilteredData(enrichedData);

        // Calculate stats
        const integratedData = enrichedData.filter(item => item.status === 'Sudah Terintegrasi').length;
        const nonIntegratedData = enrichedData.filter(item => item.status !== 'Sudah Terintegrasi').length;

        // Hitung statistik IGT (termasuk IGT terintegrasi unik)
        const { totalUniqueIGT, uniqueIntegratedIGT } = calculateIGTStats(enrichedData);

        setStats({
          totalData: enrichedData.length,
          integratedData: uniqueIntegratedIGT, // Sekarang menggunakan uniqueIntegratedIGT
          nonIntegratedData: totalUniqueIGT - uniqueIntegratedIGT, // Hitung yang belum terintegrasi
          totalUniqueIGT,
          uniqueIntegratedIGT
        });
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError('');
          setDataList([]);
          setFilteredData([]);
          setStats({ totalData: 0, integratedData: 0, nonIntegratedData: 0, totalUniqueIGT: 0, uniqueIntegratedIGT: 0 });
        } else {
          setError('Belum ada data yang diupload, Silahkan klik Tambah Data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, isGroupView]);

  const handleSearch = () => {
    setSearchLoading(true);
    setTimeout(() => {
      let filtered = [...dataList];

      // Apply text search
      if (searchText) {
        const query = searchText.toLowerCase();
        filtered = filtered.filter((row) =>
          row.no_tiket.toLowerCase().includes(query) ||
          row.nama_igt.toLowerCase().includes(query) ||
          row.kl.toLowerCase().includes(query) ||
          row.status.toLowerCase().includes(query) ||
          row.verifikator?.toLowerCase().includes(query) ||
          (row.last_updater_username ? row.last_updater_username.toLowerCase().includes(query) : false)
        );
      }

      // Apply status filter
      if (activeFilter === 'integrated') {
        filtered = filtered.filter(row => row.status === 'Sudah Terintegrasi');
      } else if (activeFilter === 'non-integrated') {
        filtered = filtered.filter(row => row.status !== 'Sudah Terintegrasi');
      }

      setFilteredData(filtered.sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified)));
      setSearchLoading(false);
    }, 500);
  };

  const scrollToTable = () => {
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleStatCardClick = (type) => {
    if (type === 'all') {
      setActiveFilter(null);
      setFilterTitle('Semua Data');
      setFilteredData([...dataList].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified)));
    } else if (type === 'integrated') {
      setActiveFilter('integrated');
      setFilterTitle('Data Sudah Terintegrasi');
      const filtered = dataList.filter(item => item.status === 'Sudah Terintegrasi');
      setFilteredData([...filtered].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified)));
    } else if (type === 'non-integrated') {
      setActiveFilter('non-integrated');
      setFilterTitle('Data Belum Terintegrasi');
      const filtered = dataList.filter(item => item.status !== 'Sudah Terintegrasi');
      setFilteredData([...filtered].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified)));
    } else if (type === 'igt-list') {
      setActiveFilter('igt-list');
      setExpandedIGT(null); // Reset expanded state
      setFilterTitle('Daftar Kode IGT');

      const tableData = igtData
        .map(igt => {
          const referenceData = igt.records && igt.records.length > 0 ? igt.records[0] : null;
          return {
            no_tiket: igt.code,
            nama_igt: referenceData ? referenceData.nama_igt : `IGT ${igt.code}`,
            kl: `${igt.count} Data`,
            status: `${igt.integratedCount} Terintegrasi / ${igt.nonIntegratedCount} Belum`,
            verifikator: '-',
            last_modified: '-',
            is_igt_summary: true,
            igt_code: igt.code,
            isExpanded: false
          };
        })
        .sort((a, b) => a.igt_code.localeCompare(b.igt_code));

      setIgtTableData(tableData);
      setFilteredData(tableData);
    }

    scrollToTable();
  };

  const handleIGTClick = (igtCode) => {
    if (activeFilter === 'igt-list' && expandedIGT === igtCode) {
      setExpandedIGT(null);
      setFilterTitle('Daftar Kode IGT');
      setFilteredData([...igtTableData]);
    } else {
      setExpandedIGT(igtCode);
      setFilterTitle(`Detail Data IGT: ${igtCode}`);

      const detailData = dataList
        .filter(item => {
          const itemIgtCode = extractIGTCode(item.no_tiket);
          return itemIgtCode === igtCode;
        })
        .sort((a, b) => a.no_tiket.localeCompare(b.no_tiket))
        .map(item => ({
          ...item,
          is_detail_row: true
        }));

      const newFilteredData = [];

      const sortedIgtTable = [...igtTableData].sort((a, b) => a.igt_code.localeCompare(b.igt_code));

      sortedIgtTable.forEach(summaryRow => {
        newFilteredData.push({
          ...summaryRow,
          isExpanded: summaryRow.igt_code === igtCode
        });

        if (summaryRow.igt_code === igtCode) {
          newFilteredData.push(...detailData);
        }
      });

      setFilteredData(newFilteredData);
    }

    scrollToTable();
  };

  const handleChartClick = (data) => {
    if (data && data.name) {
      if (data.name === "Sudah Terintegrasi") {
        setActiveFilter('integrated');
        setFilterTitle('Data Sudah Terintegrasi');
        const filtered = dataList.filter(item => item.status === 'Sudah Terintegrasi');
        setFilteredData([...filtered].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified)));
      } else {
        setActiveFilter('custom');
        setFilterTitle(`Data ${data.name}`);
        const filtered = dataList.filter(item => item.status === data.name);
        setFilteredData([...filtered].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified)));
      }
    }

    scrollToTable();
  };

  const handleDeleteClick = (ticketNo) => {
    setTicketNoToDelete(ticketNo);
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_URL}/delete/data`, { ticketNo: ticketNoToDelete }, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      // Update the data list and stats after deletion
      const updatedData = dataList.filter(item => item.no_tiket !== ticketNoToDelete);
      setDataList(updatedData);
      setFilteredData(prevData => prevData.filter(item => item.no_tiket !== ticketNoToDelete));

      // Recalculate stats
      const integratedData = updatedData.filter(item => item.status === 'Sudah Terintegrasi').length;
      const nonIntegratedData = updatedData.filter(item => item.status !== 'Sudah Terintegrasi').length;

      // Hitung ulang statistik IGT
      const { totalUniqueIGT, uniqueIntegratedIGT } = calculateIGTStats(updatedData);

      setStats({
        totalData: updatedData.length,
        integratedData: uniqueIntegratedIGT,
        nonIntegratedData: totalUniqueIGT - uniqueIntegratedIGT,
        totalUniqueIGT,
        uniqueIntegratedIGT
      });

      setShowModal(false);
    } catch (error) {
      console.error('Error delete ticket:', error);
    }
  };

  const columns = [
    {
      name: 'No Tiket',
      selector: row => row.no_tiket,
      sortable: activeFilter !== 'igt-list', // Disable sorting when in IGT list mode
      width: '200px',
      sortFunction: (a, b) => {
        // Custom sort function that preserves IGT expansion structure
        if (activeFilter === 'igt-list') {
          // Don't sort when in IGT list mode to preserve structure
          return 0;
        }
        return a.no_tiket.localeCompare(b.no_tiket);
      },
      cell: row => {
        if (row.is_igt_summary) {
          return (
            <span
              className="igt-link"
              onClick={() => handleIGTClick(row.igt_code)}
              data-tooltip-id="igt-tooltip"
              data-tooltip-content={`${expandedIGT === row.igt_code ? 'Sembunyikan' : 'Tampilkan'} detail data IGT ${row.igt_code}`}
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
            >
              <span style={{ marginRight: '5px' }}>
                {expandedIGT === row.igt_code ? '▼' : '▶'}
              </span>
              <strong>{row.no_tiket}</strong>
            </span>
          );
        }
        return row.no_tiket;
      }
    },
    {
      name: 'Nama IGT',
      selector: row => row.nama_igt,
      sortable: activeFilter !== 'igt-list', // Disable sorting when in IGT list mode
      width: '300px',
      cell: row => {
        const cellStyle = row.is_detail_row ? {
          fontSize: '0.9em',
          color: '#666'
        } : {};

        return (
          <div style={cellStyle}>
            {row.nama_igt.length > 100 ? `${row.nama_igt.substring(0, 100)}...` : row.nama_igt}
          </div>
        );
      }
    },
    {
      name: 'Updated Oleh',
      selector: row => row.last_updater_username || '-',
      sortable: activeFilter !== 'igt-list',
      width: '180px',
      omit: activeFilter === 'igt-list' && !expandedIGT,
      cell: row => {
        const cellStyle = row.is_detail_row ? {
          fontSize: '0.9em',
          color: '#666'
        } : {};

        return (
          <div
            data-tooltip-id="updater-tooltip"
            data-tooltip-content={row.last_updater_username || '-'}
            style={cellStyle}
          >
            {row.last_updater_username && row.last_updater_username.length > 30 ? `${row.last_updater_username.substring(0, 30)}...` : row.last_updater_username || '-'}
          </div>
        );
      }
    },
    {
      name: 'KL',
      selector: row => row.kl,
      sortable: activeFilter !== 'igt-list', // Disable sorting when in IGT list mode
      width: '200px',
      cell: row => {
        const cellStyle = row.is_detail_row ? {
          fontSize: '0.9em',
          color: '#666'
        } : {};

        return (
          <div
            data-tooltip-id="kl-tooltip"
            data-tooltip-content={row.kl}
            style={cellStyle}
          >
            {row.kl.length > 50 ? `${row.kl.substring(0, 50)}...` : row.kl}
          </div>
        );
      }
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: activeFilter !== 'igt-list', // Disable sorting when in IGT list mode
      width: '150px',
      cell: row => {
        // Jika ini adalah ringkasan IGT, tampilkan tanpa styling khusus
        if (row.is_igt_summary) {
          return <span>{row.status}</span>;
        }

        const statusClass = {
          'Sesuai': 'status-sesuai',
          'Tidak Sesuai': 'status-tidak-sesuai',
          'Perlu Perbaikan': 'status-perlu-perbaikan',
          'Sedang Verifikasi': 'status-sedang-verifikasi',
          'Sudah Terintegrasi': 'status-sudah-terintegrasi',
          'Belum Diverifikasi': 'status-belum-diverifikasi',
          'Revisi Pengajuan': 'status-revisi-pengajuan',
          'Disetujui': 'status-disetujui'
        }[row.status] || 'badge';

        const badgeStyle = row.is_detail_row ? {
          fontSize: '0.8em',
          padding: '2px 6px'
        } : {};

        return (
          <span
            className={`badge ${statusClass}`}
            style={badgeStyle}
          >
            {row.status}
          </span>
        );
      },
    },
    {
      name: 'Verifikator',
      selector: row => row.verifikator || '-',
      sortable: activeFilter !== 'igt-list', // Disable sorting when in IGT list mode
      width: '150px',
      omit: activeFilter === 'igt-list' && !expandedIGT,
      cell: row => {
        const cellStyle = row.is_detail_row ? {
          fontSize: '0.9em',
          color: '#666'
        } : {};

        return (
          <div
            data-tooltip-id="verifikator-tooltip"
            data-tooltip-content={row.verifikator || '-'}
            style={cellStyle}
          >
            {row.verifikator && row.verifikator.length > 30 ? `${row.verifikator.substring(0, 30)}...` : row.verifikator || '-'}
          </div>
        );
      }
    },
    {
      name: 'Terakhir Diupdate',
      selector: row => row.last_modified ? new Date(row.last_modified).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }) : '-',
      sortable: activeFilter !== 'igt-list', // Disable sorting when in IGT list mode
      width: '150px',
      omit: activeFilter === 'igt-list' && !expandedIGT,
      cell: row => {
        const cellStyle = row.is_detail_row ? {
          fontSize: '0.9em',
          color: '#666'
        } : {};

        const displayDate = row.last_modified ? new Date(row.last_modified).toLocaleDateString('id-ID', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }) : '-';

        return <div style={cellStyle}>{displayDate}</div>;
      }
    },
    {
      name: 'Aksi',
      cell: row => {
        if (row.is_igt_summary) {
          return null;
        }
        const isOwner = String(row.userid) === String(userId);
        const canDelete = isOwner; 

        return (
          <div className="actions">
            <Link to={`/kl/data/${btoa(row.no_tiket)}`} className="action-button view-button">
              <FaEye />
              <span>Lihat</span>
            </Link>
            {canDelete && (
              <button
                className="action-button delete-button"
                onClick={() => handleDeleteClick(row.no_tiket)}
                title="Hapus"
              >
                <FaTrash />
                <span>Hapus</span>
              </button>
            )}
          </div>
        );
      },
      width: '180px',
      omit: activeFilter === 'igt-list' && !expandedIGT,
    },
  ];

  const conditionalRowStyles = [
    {
      when: row => row.is_igt_summary,
      style: {
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
        borderLeft: '4px solid #007bff',
      },
    },
    {
      when: row => row.is_detail_row,
      style: {
        backgroundColor: '#fafbfc',
        borderLeft: '2px solid #e9ecef',
        paddingLeft: '20px',
      },
    },
  ];

  const menuItems = getKLMenuItems();

  return (
    <DashboardLayout menuItems={menuItems} userRole="K/L">
      {/* Email Reminder Banner */}
      <EmailReminderBanner userEmail={userEmail} profilePath="/kl/profile" />
      
      <div className="container mt-4">
        {/* Welcome Card */}
        <div className="card welcome-card mb-4">
          <div className="card-body">
            <h2 className="welcome-title">Selamat Datang, {username}!</h2>
            <p className="welcome-text">Dashboard Monitoring Data IGT Anda</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-4">
          {/* Total Data */}
          <div className="col-md-3">
            <div
              className={`card stat-card stat-total ${activeFilter === null ? 'active' : ''}`}
              onClick={() => handleStatCardClick('all')}
            >
              <div className="card-body">
                <h5 className="stat-title">Total Data</h5>
                <h2 className="stat-value">{stats.totalData}</h2>
                <p className="stat-desc">Jumlah seluruh data yang diupload</p>
                <div className="card-overlay">
                  <span>Lihat Semua Data</span>
                </div>
              </div>
            </div>
          </div>

          {/* Total IGT */}
          <div className="col-md-3">
            <div
              className={`card stat-card stat-igt ${activeFilter === 'igt-list' ? 'active' : ''}`}
              onClick={() => handleStatCardClick('igt-list')}
            >
              <div className="card-body">
                <h5 className="stat-title">Total IGT</h5>
                <h2 className="stat-value">{stats.totalUniqueIGT}</h2>
                <p className="stat-desc">Jumlah Data IGT</p>
                <div className="card-overlay">
                  <span>Lihat Daftar IGT</span>
                </div>
              </div>
            </div>
          </div>

          {/* Data Sudah Terintegrasi */}
          <div className="col-md-3">
            <div
              className={`card stat-card stat-integrated ${activeFilter === 'integrated' ? 'active' : ''}`}
              onClick={() => handleStatCardClick('integrated')}
            >
              <div className="card-body">
                <h5 className="stat-title">Data Sudah Terintegrasi</h5>
                <h2 className="stat-value">{stats.integratedData}</h2>
                <p className="stat-desc">Jumlah IGT yang sudah terintegrasi</p>
                <div className="card-overlay">
                  <span>Lihat Data Terintegrasi</span>
                </div>
              </div>
            </div>
          </div>

          {/* Data Belum Terintegrasi */}
          <div className="col-md-3">
            <div
              className={`card stat-card stat-non-integrated ${activeFilter === 'non-integrated' ? 'active' : ''}`}
              onClick={() => handleStatCardClick('non-integrated')}
            >
              <div className="card-body">
                <h5 className="stat-title">Data Belum Terintegrasi</h5>
                <h2 className="stat-value">{stats.nonIntegratedData}</h2>
                <p className="stat-desc">Jumlah IGT yang belum terintegrasi</p>
                <div className="card-overlay">
                  <span>Lihat Data Belum Terintegrasi</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Chart */}
        <div className="card shadow-lg p-4 mb-4">
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <div className="search-container">
              <input
                type="text"
                className="filter-input"
                placeholder="Cari No Tiket, Nama, KL, Status, atau Updated Oleh"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="btn btn-primary search-button"
                onClick={handleSearch}
                disabled={searchLoading}
              >
                <i className="fas fa-search"></i> Cari
              </button>
              <Link to="/kl/upload" className="btn btn-success ms-2">
                <i className="fas fa-plus"></i> Tambah Data
              </Link>
            </div>
          </div>

          {/* Filter aktif */}
          {activeFilter && (
            <div className="active-filter-container mt-3">
              <div className="active-filter-badge">
                <span>Filter aktif: <strong>{filterTitle}</strong></span>
                <button
                  className="btn btn-sm btn-light ms-2"
                  onClick={() => {
                    setActiveFilter(null);
                    setExpandedIGT(null);
                    setFilterTitle('Semua Data');
                    setFilteredData([...dataList].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified)));
                  }}
                >
                  &times; Tampilkan Semua
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Chart */}
        <div className="card shadow-lg p-4 mb-4">
          <DataChart
            data={dataList}
            onPieClick={handleChartClick}
            activeFilter={activeFilter}
          />
        </div>

        {/* Toggle Data */}
        <div className="d-flex justify-content-end mb-2">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="groupViewSwitchTop"
              checked={isGroupView}
              onChange={(e) => {
                setActiveFilter(null);
                setExpandedIGT(null);
                setFilterTitle(e.target.checked ? 'Semua Data Saya' : 'Semua Data');
                setIsGroupView(e.target.checked);
              }}
            />
            <label className="form-check-label" htmlFor="groupViewSwitchTop">
              Tampilkan Data Saya
            </label>
          </div>
        </div>

        {/* Data Table */}
        <div className="card shadow-lg p-4 printable" ref={tableRef}>
          <h3 className="title">
            {filterTitle}
            <span className="badge bg-primary ms-2">{filteredData.length} Data</span>
          </h3>

          {error && <div className="alert-danger">{error}</div>}

          <DataTable
            columns={columns}
            data={filteredData}
            progressPending={loading}
            progressComponent={<LoadingSpinner message="Memuat data..." type="spinner" size="medium" />}
            pagination
            highlightOnHover
            pointerOnHover
            defaultSortFieldId={activeFilter === 'igt-list' ? undefined : 1}
            defaultSortAsc={false}
            noDataComponent={<p className="text-center text-muted">Tidak ada data yang tersedia.</p>}
            responsive
            conditionalRowStyles={conditionalRowStyles}
          />
        </div>
        <Tooltip id="ticket-tooltip" />
        <Tooltip id="nama-igt-tooltip" />
        <Tooltip id="kl-tooltip" />
        <Tooltip id="verifikator-tooltip" />
        <Tooltip id="updater-tooltip" />
        <Tooltip id="igt-tooltip" />
      
      {searchLoading && (
        <div className="loading-overlay-fullscreen">
          <LoadingSpinner message="Mencari data..." type="dots" size="large" />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Konfirmasi Hapus</h3>
              </div>
              <div className="modal-body">
                <p>Apakah Anda yakin ingin menghapus tiket No <strong>{ticketNoToDelete}</strong>?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-danger" onClick={handleDelete}>Hapus</button>
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default UserPage;